import { DateTime } from "luxon";

import { PatternStatus } from "./PatternLifecycleDTO";
import { MaterialUnit, StorageClass } from "./ExplosivesDTO";

export enum HoleStatus {
  Designed = 0,
  Drilled = 1,
  RedrillRequired = 2,
  BackfillRequired = 3,
  Dipped = 4,
  Charged = 5,
  Stemmed = 6,
  Fired = 7,
  Abandoned = 8,
  Misfire = 9,
}

export const HOLE_STATUS_LABELS: Record<HoleStatus, string> = {
  [HoleStatus.Designed]: "Designed",
  [HoleStatus.Drilled]: "Drilled",
  [HoleStatus.RedrillRequired]: "Redrill required",
  [HoleStatus.BackfillRequired]: "Backfill required",
  [HoleStatus.Dipped]: "Dipped",
  [HoleStatus.Charged]: "Charged",
  [HoleStatus.Stemmed]: "Stemmed",
  [HoleStatus.Fired]: "Fired",
  [HoleStatus.Abandoned]: "Abandoned",
  [HoleStatus.Misfire]: "Misfire",
};

export enum DrillOutcome {
  Complete = 0,
  Short = 1,
  Collapsed = 2,
  Blocked = 3,
  Abandoned = 4,
}

export const DRILL_OUTCOME_LABELS: Record<DrillOutcome, string> = {
  [DrillOutcome.Complete]: "Complete",
  [DrillOutcome.Short]: "Short",
  [DrillOutcome.Collapsed]: "Collapsed",
  [DrillOutcome.Blocked]: "Blocked",
  [DrillOutcome.Abandoned]: "Abandoned",
};

export enum DrillingEntryMode {
  Summary = 0,
  Hole = 1,
}

export enum DetectionSource {
  Manual = 0,
  GpsDerived = 1,
  VendorCode = 2,
  Imported = 3,
}

export enum DipCondition {
  Dry = 0,
  Wet = 1,
  Dewatered = 2,
  Collapsed = 3,
  Blocked = 4,
  Cavity = 5,
}

export enum DipAction {
  None = 0,
  Backfill = 1,
  CleanOut = 2,
  Redrill = 3,
  Abandon = 4,
}

export const DIP_CONDITION_LABELS: Record<DipCondition, string> = {
  [DipCondition.Dry]: "Dry",
  [DipCondition.Wet]: "Wet",
  [DipCondition.Dewatered]: "Dewatered",
  [DipCondition.Collapsed]: "Collapsed",
  [DipCondition.Blocked]: "Blocked",
  [DipCondition.Cavity]: "Cavity",
};

export const DIP_ACTION_LABELS: Record<DipAction, string> = {
  [DipAction.None]: "None",
  [DipAction.Backfill]: "Backfill",
  [DipAction.CleanOut]: "Clean out",
  [DipAction.Redrill]: "Redrill",
  [DipAction.Abandon]: "Abandon",
};

export interface BlastHoleDTO {
  id: number;
  holeName: string;
  holeTypeId: number;
  holeType: string;
  designCollarE: number | null;
  designCollarN: number | null;
  designCollarRl: number | null;
  designDip: number | null;
  designAzimuth: number | null;
  designDepthM: number | null;
  designBitSizeId: number | null;
  designBitSize: string | null;
  designDelayMs: number | null;
  designStemmingM: number | null;
  planE: number | null;
  planN: number | null;
  status: HoleStatus;
  attempts: number;
  drilledDepthM: number | null;
  lastOutcome: DrillOutcome | null;
  lastDrilledAt: DateTime | null;
  dipDepthM: number | null;
  waterDepthM: number | null;
  dipCondition: DipCondition | null;
  dipAction: DipAction | null;
  dippedAt: DateTime | null;
  chargedKg: number;
  stemmedM: number | null;
  decks: number;
  isWet: boolean;
  rowVersion: string;
}

export interface PatternHolesDTO {
  patternBlastId: number;
  reference: string;
  patternStatus: PatternStatus;
  canEdit: boolean;
  depthToleranceM: number;
  holes: BlastHoleDTO[];
  sheets?: WorkSheetDTO[];
}

export enum WorkSheetKind {
  Drill = 0,
  Dip = 1,
  Charge = 2,
}

export interface WorkSheetDTO {
  id: number;
  kind: WorkSheetKind;
  name: string;
  issuedToPersonId: number | null;
  issuedTo: string | null;
  issuedAt: DateTime;
  closedAt: DateTime | null;
  holeIds: number[];
  rowVersion: string;
}

export interface IssueWorkSheetRequest {
  kind: WorkSheetKind;
  name: string | null;
  issuedToPersonId: number | null;
  holeIds: number[];
}

export interface DrilledHoleDTO {
  id: number;
  blastHoleId: number;
  holeName: string;
  depthM: number;
  manualDepthM: number | null;
  importedDepthM: number | null;
  designDepthM: number | null;
  outcome: DrillOutcome;
  waterEncountered: boolean;
  isRedrill: boolean;
  bitSizeId: number;
  bitSize: string;
  completedAt: DateTime | null;
  detectionSource: DetectionSource;
  accepted: boolean;
  rowVersion: string;
}

export interface DrillingSessionHolesDTO {
  drillingBlastId: number;
  patternBlastId: number;
  reference: string;
  entryMode: DrillingEntryMode;
  canChangeMode: boolean;
  attempts: DrilledHoleDTO[];
  holes: PatternHolesDTO;
}

export interface SaveDrilledHoleRequest {
  drillingBlastId: number;
  blastHoleId: number;
  depthM: number;
  outcome: DrillOutcome;
  waterEncountered: boolean;
  bitSizeId: number | null;
  completedAt: DateTime | null;
  acceptOutsideTolerance: boolean;
  rowVersion: string | null;
}

export interface DrilledHoleSaveResultDTO {
  saved: boolean;
  warnings: string[];
  attempt: DrilledHoleDTO | null;
}

export interface HoleDipDTO {
  id: number;
  blastHoleId: number;
  holeName: string;
  dippedAt: DateTime;
  dippedByPersonId: number | null;
  dippedBy: string | null;
  depthM: number;
  waterDepthM: number | null;
  condition: DipCondition;
  action: DipAction;
  backfillM: number | null;
  designDepthM: number | null;
  detectionSource: DetectionSource;
  rowVersion: string;
}

export interface SaveHoleDipRequest {
  blastHoleId: number;
  dippedAt: DateTime | null;
  dippedByPersonId: number | null;
  depthM: number;
  waterDepthM: number | null;
  condition: DipCondition;
  action: DipAction;
  backfillM: number | null;
  acceptOutsideTolerance: boolean;
  rowVersion: string | null;
}

export interface HoleDipSaveResultDTO {
  saved: boolean;
  warnings: string[];
  dip: HoleDipDTO | null;
}

export interface ChargeMaterialDTO {
  id: number;
  name: string;
  unit: MaterialUnit;
  storageClass: StorageClass;
}

export interface HoleChargeLineDTO {
  id: number;
  deckNo: number;
  blastMaterialId: number;
  material: string;
  unit: MaterialUnit;
  storageClass: StorageClass;
  designQuantity: number | null;
  actualQuantity: number;
  fromDepthM: number | null;
  toDepthM: number | null;
}

export interface HoleChargeDTO {
  blastHoleId: number;
  holeName: string;
  chargedAt: DateTime | null;
  lines: HoleChargeLineDTO[];
  explosiveKg: number;
  chargeLengthM: number | null;
  stemmingM: number | null;
  designChargeLengthM: number | null;
  designStemmingM: number | null;
  flags: string[];
  columnVersion: string;
}

export interface PatternChargingDTO {
  patternBlastId: number;
  holes: HoleChargeDTO[];
  materials: ChargeMaterialDTO[];
  depthToleranceM: number;
  chargeLengthToleranceM?: number;
  stemmingToleranceM?: number;
  dipMaxAgeHours: number;
}

export interface ChargeLineInput {
  deckNo: number;
  blastMaterialId: number;
  designQuantity: number | null;
  actualQuantity: number;
  fromDepthM: number | null;
  toDepthM: number | null;
}

export interface SaveHoleChargeRequest {
  chargedAt: DateTime | null;
  lines: ChargeLineInput[];
  acceptFlags: boolean;
  columnVersion: string | null;
}

export interface HoleChargeSaveResultDTO {
  saved: boolean;
  warnings: string[];
  charge: HoleChargeDTO | null;
}

export interface IredesImportResultDTO {
  added: number;
  merged: number;
  updated: number;
  skipped: number;
  warnings: string[];
  session: DrillingSessionHolesDTO;
}

export interface HoleDesignMappingDTO {
  name: string | null;
  fields: Record<string, string>;
  diameterUnit: string;
  dipFromVertical: boolean;
}

export interface HoleDesignPreviewDTO {
  headers: string[];
  sample: string[][];
  rows: number;
  fields: string[];
  suggested: HoleDesignMappingDTO;
  saved: HoleDesignMappingDTO[];
}

export interface HoleDesignImportResultDTO {
  added: number;
  updated: number;
  removed: number;
  keptWithHistory: number;
  warnings: string[];
  holes: PatternHolesDTO;
}

export const DESIGN_FIELD_LABELS: Record<string, string> = {
  HoleName: "Hole name",
  HoleType: "Hole type",
  CollarE: "Collar easting",
  CollarN: "Collar northing",
  CollarRl: "Collar RL",
  ToeE: "Toe easting",
  ToeN: "Toe northing",
  ToeRl: "Toe RL",
  Dip: "Dip",
  Azimuth: "Azimuth",
  Length: "Length",
  Diameter: "Diameter",
  DelayMs: "Delay (ms)",
  StemmingM: "Stemming (m)",
};
